import includes from "array-includes";
import update from "immutability-helper";
import Leaflet from "leaflet";
import "proj4leaflet";
import {
  LIST,
  GET,
  ADD_PROPOSAL,
  REMOVE_PROPOSAL,
  SET_PROPOSALS,
  SET_LAST_VISITED,
  SET_DRAFTS
} from "./constants";

import unknownIcon from "app/resources/unknown.svg";

const leaflet = {
  crs: {
    "EPSG:3395": Leaflet.CRS.EPSG3395,
    "EPSG:3857": Leaflet.CRS.EPSG3857,
    "EPSG:4326": Leaflet.CRS.EPSG4326,
    "EPSG:900913": Leaflet.CRS.EPSG900913
  },
  layers: {}
};

const initialState = {
  topicsById: {},
  topicIds: [],
  fetched: 0,
  actions: { [GET]: {} },
  lastVisited: {}
};

const handleAddProposal = (state, action) => {
  const { proposalId, topicId } = action;
  const topic = state.topicsById[topicId];
  const { proposals } = topic;
  const topicsById = {
    ...state.topicsById,
    [topicId]: {
      ...topic,
      proposals: [...proposals, proposalId]
    }
  };
  return {
    ...state,
    topicsById
  };
};

const handleRemoveProposal = (state, action) => {
  const { proposalId, topicId } = action;
  const topic = state.topicsById[topicId];
  const { proposals } = topic;
  const topicsById = {
    ...state.topicsById,
    [topicId]: {
      ...topic,
      proposals: proposals.filter(id => id !== proposalId)
    }
  };
  return {
    ...state,
    topicsById
  };
};

const updateLeafletCrs = crsConf => {
  for (let key in crsConf) {
    let { code, proj4def, options } = crsConf[key];
    leaflet.crs[key] = new Leaflet.Proj.CRS(code, proj4def, options);
  }
};

const updateLeafletLayers = layerConf => {
  for (let key in layerConf) {
    let {
      options: { crs }
    } = layerConf[key];
    leaflet.layers[key] = update(layerConf[key], {
      options: { crs: { $set: leaflet.crs[crs] } }
    });
  }
};

const setLayers = (layers = []) => {
  return layers.map(layer =>
    update(layer, { layer: { $set: leaflet.layers[layer.name] } })
  );
};

const normalizeLeafletConfig = config => {
  const { crs, layers } = config;
  updateLeafletCrs(crs);
  updateLeafletLayers(layers);
  return update(config, {
    layersControl: {
      baseLayers: { $apply: setLayers },
      overlays: { $apply: setLayers }
    },
    options: {
      crs: { $apply: code => leaflet.crs[code] || leaflet.crs["EPSG:3857"] }
    },
    $unset: ["crs", "layers"]
  });
};

const normalizeObjectsConfig = config => {
  if (!config) {
    return [];
  }
  return config;
};

const normalizeTopic = topic => {
  const normalized = update(topic, {
    $merge: {
      proposals: [],
      drafts: [],
      published: [],
      pathName: topic._id,
      icon: topic.icon || unknownIcon
    },
    config: {
      leaflet: { $apply: normalizeLeafletConfig },
      objects: { $apply: normalizeObjectsConfig },
      has3d: { $set: true }
    }
  });
  return normalized;
};

const handleListSuccess = (state, action) => {
  const topicsById = {};
  const topicIds = [];
  for (const topic of action.data) {
    topicsById[topic._id] = normalizeTopic(topic);
    topicIds.push(topic._id);
  }
  return update(state, {
    $merge: {
      topicsById,
      topicIds,
      fetched: Date.now()
    },
    actions: { [LIST]: { $set: action } }
  });
};

const handleGetSuccess = (state, action) => {
  const isKnown = state.topicIds.indexOf(action.data._id) >= 0;
  const topic = update(state.topicsById[action.data._id] || {}, {
    $merge: normalizeTopic(action.data)
  });

  const topicsById = update(state.topicsById, {
    $merge: {
      [action.data._id]: topic
    }
  });
  return update(state, {
    $merge: { topicsById },
    topicIds: { $push: isKnown ? [] : [action.data._id] },
    actions: { [GET]: { [action.payload.params.topicId]: { $set: action } } }
  });
};

const mergeProposals = topic => {
  let a = topic.drafts;
  let b = topic.published;
  let c = topic.proposals;
  const ab = a.concat(b);
  a = a.filter(e => !includes(c, e));
  b = b.filter(e => !includes(c, e));
  c = c.filter(e => includes(ab, e));
  topic.proposals = c.concat(a.concat(b));
  return topic;
};

const handleSetPublished = (state, action) => {
  const { topicId, proposals } = action;
  const ids = proposals.map(({ _id }) => _id);
  let topic = update(state.topicsById[topicId], {
    published: { $set: ids }
  });
  topic = mergeProposals(topic);
  return update(state, {
    topicsById: {
      [topicId]: { $set: topic }
    }
  });
};

const handleSetDrafts = (state, action) => {
  const { topicId, drafts } = action;
  const ids = drafts.map(({ _id }) => _id);
  let topic = update(state.topicsById[topicId], {
    drafts: { $set: ids }
  });
  topic = mergeProposals(topic);
  return update(state, {
    topicsById: {
      [topicId]: { $set: topic }
    }
  });
};

const handleSetLastVisited = (state, action) => {
  const { topicId, proposalId } = action;
  return update(state, { lastVisited: { $set: { topicId, proposalId } } });
};

export default (state = initialState, action) => {
  const { topicId, proposals, drafts } = action;
  switch (action.type) {
    case ADD_PROPOSAL:
      return handleAddProposal(state, action);
    case REMOVE_PROPOSAL:
      return handleRemoveProposal(state, action);
    case SET_PROPOSALS:
      return handleSetPublished(state, { topicId, proposals });
    case SET_DRAFTS:
      return handleSetDrafts(state, { topicId, drafts });
    case SET_LAST_VISITED:
      return handleSetLastVisited(state, action);
    case LIST.PENDING:
      return update(state, {
        actions: { [LIST]: { $set: action } }
      });
    case LIST.SUCCESS:
      return handleListSuccess(state, action);
    case LIST.ERROR:
      return update(state, {
        actions: { [LIST]: { $set: action } }
      });
    case GET.PENDING:
      return update(state, {
        actions: {
          [GET]: { [action.payload.params.topicId]: { $set: action } }
        }
      });
    case GET.SUCCESS:
      return handleGetSuccess(state, action);
    case GET.ERROR:
      return update(state, {
        actions: {
          [GET]: { [action.payload.params.topicId]: { $set: action } }
        }
      });
    default:
      return state;
  }
};



// WEBPACK FOOTER //
// ./src/app/topics/reducers.js